import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Button, Table, Tabs } from '@vwfs-bronson/bronson-react';
import { AuthTokens } from '../../auth';
import {
  FesConfig,
  Agent,
  Team,
  UsageBreakdown,
  UsageFacet,
  UsageReport,
  UsageReportFilters,
  getAllTeams,
  getAllAgents,
  getUsageReport,
} from '../../fes';

const numberFormatter = new Intl.NumberFormat('en-US');
const costFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const compactNumberFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 1,
});
const chartDateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});

const categoryLabels: Record<string, string> = {
  chat: 'Chat',
  'github-pr': 'GitHub pull request',
  jira: 'Jira',
  'azure-devops': 'Azure DevOps',
  pipeline: 'Pipeline',
  api: 'API',
  unavailable: 'Unavailable',
};

const emptyFilters: UsageReportFilters = {};
const advancedFilterKeys: Array<keyof UsageReportFilters> = [
  'userId',
  'agentId',
  'repository',
  'category',
  'skill',
  'model',
];
const shareableFilterKeys: Array<keyof UsageReportFilters> = [
  'teamId',
  ...advancedFilterKeys,
];

function currentUtcRange(): { from: string; to: string } {
  const today = new Date().toISOString().slice(0, 10);
  return { from: `${today.slice(0, 8)}01`, to: today };
}

function isDateParameter(value: string | null): value is string {
  return Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`)));
}

function readUsageRoute(): { from: string; to: string; filters: UsageReportFilters } {
  const defaults = currentUtcRange();
  const params = new URLSearchParams(window.location.search);
  const from = params.get('from');
  const to = params.get('to');
  const filters: UsageReportFilters = {};
  shareableFilterKeys.forEach(key => {
    const value = params.get(key);
    if (value) filters[key] = value;
  });
  return {
    from: isDateParameter(from) ? from : defaults.from,
    to: isDateParameter(to) ? to : defaults.to,
    filters,
  };
}

function writeUsageRoute(from: string, to: string, filters: UsageReportFilters): void {
  const params = new URLSearchParams({ from, to });
  shareableFilterKeys.forEach(key => {
    if (filters[key]) params.set(key, filters[key]!);
  });
  window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
}

function csvCell(value: string | number): string {
  const raw = String(value);
  const text = /^[=+\-@]/.test(raw) ? `'${raw}` : raw;
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function buildUsageReportCsv(
  report: UsageReport,
  teamNames: Map<string, string>,
  agentNames: Map<string, string> = new Map(),
): string {
  const header = [
    'Breakdown',
    'Value',
    'Jobs',
    'Job share',
    'Priced jobs',
    'Unpriced jobs',
    'Input tokens',
    'Output tokens',
    'Cached input tokens',
    'Reasoning output tokens',
    'Total tokens',
    'Cost USD',
    'From',
    'To',
  ];
  const row = (
    kind: string,
    value: string,
    item: UsageBreakdown | UsageReport['summary'],
    jobShare = '',
  ) => [
    kind,
    value,
    item.jobs,
    jobShare,
    item.pricedJobs,
    item.unpricedJobs,
    item.inputTokens,
    item.outputTokens,
    item.cachedInputTokens,
    item.reasoningOutputTokens,
    item.totalTokens,
    item.costUsd,
    report.from,
    report.to,
  ];
  const rows: Array<Array<string | number>> = [header, row('Summary', 'Filtered usage', report.summary)];
  report.byModel.forEach(item => rows.push(row('Model', item.model || 'unavailable', item)));
  report.byTeam.forEach(item => {
    const teamId = item.teamId || 'personal';
    rows.push(row('Team', teamNames.get(teamId) || teamId, item));
  });
  report.byUser.forEach(item => rows.push(row('User', item.userId || 'unavailable', item)));
  report.byAgent.forEach(item => rows.push(row(
    'Agent',
    item.agentName || agentNames.get(item.agentId || '') || item.agentId || 'unavailable',
    item,
  )));
  report.byRepository.forEach(item => rows.push(row('Repository', item.repository || 'unavailable', item)));
  report.byCategory.forEach(item => rows.push(row('Category', categoryLabels[item.category || ''] || item.category || 'unavailable', item)));
  report.bySkill.forEach(item => rows.push([
    'Skill attribution',
    item.skill || 'unavailable',
    item.jobs,
    report.summary.jobs ? percentFormatter.format(item.jobs / report.summary.jobs) : '0%',
    '', '', '', '', '', '', '', '',
    report.from,
    report.to,
  ]));
  report.byDay.forEach(item => rows.push(row('Day', item.date || '', item)));
  return rows.map(values => values.map(csvCell).join(',')).join('\n');
}

function downloadCsv(csv: string, from: string, to: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `aca-usage-${from}-${to}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function SummaryCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="app-usage-card">
      <span>{label}</span>
      <strong>{value}</strong>
      {hint ? <small>{hint}</small> : null}
    </div>
  );
}

function DailyUsageChart({ rows }: { rows: UsageBreakdown[] }) {
  const [activeDate, setActiveDate] = useState<string>();
  const days = [...rows].sort((left, right) => (left.date || '').localeCompare(right.date || ''));

  if (days.length === 0) {
    return <p className="app-usage-empty">No usage in this period.</p>;
  }

  const maxTokens = Math.max(...days.map(day => day.totalTokens), 1);
  const averageTokens = days.reduce((sum, day) => sum + day.totalTokens, 0) / days.length;
  const peak = days.reduce((highest, day) => day.totalTokens > highest.totalTokens ? day : highest);

  return (
    <>
      <div className="app-usage-chart-summary" aria-label="Daily usage summary">
        <span><strong>{compactNumberFormatter.format(averageTokens)}</strong> active-day average</span>
        <span><strong>{compactNumberFormatter.format(peak.totalTokens)}</strong> peak on {chartDateFormatter.format(new Date(`${peak.date}T00:00:00Z`))}</span>
      </div>
      <div className="app-usage-chart" role="group" aria-label="Daily total token usage">
        {days.map(day => {
          const percentage = Math.max(1, day.totalTokens / maxTokens * 100);
          const label = `${day.date}: ${numberFormatter.format(day.totalTokens)} tokens, ${numberFormatter.format(day.jobs)} jobs, ${costFormatter.format(day.costUsd)}`;
          return (
            <div className="app-usage-chart-row" key={day.date}>
              <span>{chartDateFormatter.format(new Date(`${day.date}T00:00:00Z`))}</span>
              <div className="app-usage-chart-track">
                <button
                  className={`app-usage-chart-bar${activeDate === day.date ? ' is-active' : ''}`}
                  type="button"
                  style={{ width: `${percentage}%` }}
                  aria-label={label}
                  onBlur={() => setActiveDate(undefined)}
                  onFocus={() => setActiveDate(day.date)}
                  onMouseEnter={() => setActiveDate(day.date)}
                  onMouseLeave={() => setActiveDate(undefined)}
                />
                {activeDate === day.date ? (
                  <div
                    className={`app-usage-chart-bar-tooltip${percentage > 70 ? ' is-right' : ''}`}
                    style={{ left: `${percentage}%` }}
                    role="status"
                  >
                    <strong>{numberFormatter.format(day.totalTokens)} tokens</strong>
                    <span>{numberFormatter.format(day.jobs)} jobs · {costFormatter.format(day.costUsd)}</span>
                  </div>
                ) : null}
              </div>
              <strong>{compactNumberFormatter.format(day.totalTokens)}</strong>
            </div>
          );
        })}
      </div>
    </>
  );
}

function BreakdownTable({
  title,
  columnTitle,
  rows,
  labelFor,
  note,
}: {
  title: string;
  columnTitle: string;
  rows: UsageBreakdown[];
  labelFor: (row: UsageBreakdown) => string;
  note?: string;
}) {
  return (
    <section className="app-usage-panel">
      <h2>{title}</h2>
      {note ? <p className="app-usage-panel-note">{note}</p> : null}
      {rows.length === 0 ? (
        <p className="app-usage-empty">No usage in this period.</p>
      ) : (
        <div className="app-table-wrap">
          <Table className="app-table" colored>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{columnTitle}</Table.Th>
                <Table.Th>Jobs</Table.Th>
                <Table.Th>Input</Table.Th>
                <Table.Th>Output</Table.Th>
                <Table.Th>Total tokens</Table.Th>
                <Table.Th>Cost</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map(row => (
                <Table.Tr key={labelFor(row)}>
                  <Table.Td>{labelFor(row)}</Table.Td>
                  <Table.Td>{numberFormatter.format(row.jobs)}</Table.Td>
                  <Table.Td>{numberFormatter.format(row.inputTokens)}</Table.Td>
                  <Table.Td>{numberFormatter.format(row.outputTokens)}</Table.Td>
                  <Table.Td>{numberFormatter.format(row.totalTokens)}</Table.Td>
                  <Table.Td>{costFormatter.format(row.costUsd)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      )}
    </section>
  );
}

function SkillAttributionTable({ rows, totalJobs }: { rows: UsageBreakdown[]; totalJobs: number }) {
  return (
    <section className="app-usage-panel">
      <h2>Skills</h2>
      <p className="app-usage-panel-note">
        Shows how often each skill was configured. A job with multiple skills appears in each skill row.
      </p>
      {rows.length === 0 ? (
        <p className="app-usage-empty">No skill attribution in this period.</p>
      ) : (
        <div className="app-table-wrap">
          <Table className="app-table" colored>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Configured skill</Table.Th>
                <Table.Th>Jobs</Table.Th>
                <Table.Th>Share of filtered jobs</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map(row => (
                <Table.Tr key={row.skill || 'unavailable'}>
                  <Table.Td>{row.skill || 'unavailable'}</Table.Td>
                  <Table.Td>{numberFormatter.format(row.jobs)}</Table.Td>
                  <Table.Td>{percentFormatter.format(totalJobs ? row.jobs / totalJobs : 0)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      )}
    </section>
  );
}

function FilterSelect({
  id,
  label,
  value,
  options,
  allLabel,
  onChange,
}: {
  id: string;
  label: string;
  value?: string;
  options: UsageFacet[];
  allLabel: string;
  onChange: (value: string) => void;
}) {
  const hasSelectedOption = !value || options.some(option => option.value === value);
  return (
    <label htmlFor={id}>
      <span>{label}</span>
      <select id={id} value={value || ''} onChange={event => onChange(event.target.value)}>
        <option value="">{allLabel}</option>
        {options.map(option => (
          <option key={option.value} value={option.value}>{option.label || option.value}</option>
        ))}
        {!hasSelectedOption ? <option value={value}>{value}</option> : null}
      </select>
    </label>
  );
}

export function UsageReportView({ fesConfig, tokens }: { fesConfig: FesConfig; tokens: AuthTokens }) {
  const initialRoute = useMemo(readUsageRoute, []);
  const [from, setFrom] = useState(initialRoute.from);
  const [to, setTo] = useState(initialRoute.to);
  const [filters, setFilters] = useState<UsageReportFilters>(initialRoute.filters);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(
    advancedFilterKeys.some(key => Boolean(initialRoute.filters[key])),
  );
  const [teams, setTeams] = useState<Team[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [report, setReport] = useState<UsageReport>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();

  const teamNames = useMemo(() => new Map(teams.map(team => [team.teamId, team.name])), [teams]);
  const agentNames = useMemo(() => new Map(agents.map(agent => [agent.agentId, agent.name])), [agents]);
  const teamOptions = useMemo(() => {
    const values = new Map<string, string>();
    report?.facets.teams.forEach(facet => values.set(
      facet.value,
      teamNames.get(facet.value) || facet.label || (facet.value === 'personal' ? 'Personal' : facet.value),
    ));
    teams.forEach(team => values.set(team.teamId, team.name));
    return Array.from(values.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [report, teamNames, teams]);
  const agentOptions = useMemo(() => (report?.facets.agents || []).map(facet => ({
    ...facet,
    label: facet.label || agentNames.get(facet.value) || facet.value,
  })), [agentNames, report]);
  const activeAdvancedFilterCount = advancedFilterKeys.filter(key => Boolean(filters[key])).length;

  const loadReport = async (
    selectedFrom: string,
    selectedTo: string,
    selectedFilters: UsageReportFilters,
  ) => {
    setIsLoading(true);
    setError(undefined);
    try {
      const nextReport = await getUsageReport(fesConfig, tokens, {
        from: selectedFrom,
        to: selectedTo,
        ...selectedFilters,
      });
      setReport(nextReport);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to load usage report.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      getAllTeams(fesConfig, tokens),
      getAllAgents(fesConfig, tokens),
      getUsageReport(fesConfig, tokens, {
        from: initialRoute.from,
        to: initialRoute.to,
        ...initialRoute.filters,
      }),
    ])
      .then(([loadedTeams, loadedAgents, loadedReport]) => {
        if (!cancelled) {
          setTeams(loadedTeams);
          setAgents(loadedAgents);
          setReport(loadedReport);
        }
      })
      .catch(loadError => {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : 'Unable to load usage report.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fesConfig, initialRoute, tokens]);

  useEffect(() => {
    const restoreRoute = () => {
      const route = readUsageRoute();
      setFrom(route.from);
      setTo(route.to);
      setFilters(route.filters);
      setShowAdvancedFilters(advancedFilterKeys.some(key => Boolean(route.filters[key])));
      void loadReport(route.from, route.to, route.filters);
    };
    window.addEventListener('popstate', restoreRoute);
    return () => window.removeEventListener('popstate', restoreRoute);
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (from > to) {
      setError('From date must be before or equal to the to date.');
      return;
    }
    writeUsageRoute(from, to, filters);
    void loadReport(from, to, filters);
  };

  const updateFilter = (key: keyof UsageReportFilters, value: string) => {
    setFilters(current => ({ ...current, [key]: value || undefined }));
  };

  const clearFilters = () => {
    setFilters(emptyFilters);
    setShowAdvancedFilters(false);
    writeUsageRoute(from, to, emptyFilters);
    void loadReport(from, to, emptyFilters);
  };

  return (
    <div className="app-usage-report">
      <form className="app-usage-filters" onSubmit={handleSubmit}>
        <label htmlFor="usage-from">
          <span>From</span>
          <input id="usage-from" type="date" value={from} max={to} onChange={event => setFrom(event.target.value)} />
        </label>
        <label htmlFor="usage-to">
          <span>To</span>
          <input id="usage-to" type="date" value={to} min={from} onChange={event => setTo(event.target.value)} />
        </label>
        <FilterSelect
          id="usage-team"
          label="Team"
          value={filters.teamId}
          options={teamOptions}
          allLabel="All teams"
          onChange={value => updateFilter('teamId', value)}
        />
        <div className="app-usage-filter-actions">
          <Button type="submit" disabled={isLoading}>{isLoading ? 'Loading...' : 'Apply'}</Button>
          <Button
            secondary
            type="button"
            aria-expanded={showAdvancedFilters}
            aria-controls="usage-advanced-filters"
            onClick={() => setShowAdvancedFilters(current => !current)}
          >
            More filters{activeAdvancedFilterCount ? ` (${activeAdvancedFilterCount})` : ''}
          </Button>
          <Button secondary type="button" disabled={isLoading} onClick={clearFilters}>Clear filters</Button>
          <Button
            secondary
            type="button"
            disabled={!report}
            onClick={() => report && downloadCsv(buildUsageReportCsv(report, teamNames, agentNames), from, to)}
          >
            Export CSV
          </Button>
        </div>
        {showAdvancedFilters ? (
          <div className="app-usage-advanced-filters" id="usage-advanced-filters">
            <FilterSelect
              id="usage-user"
              label="User"
              value={filters.userId}
              options={report?.facets.users || []}
              allLabel="All users"
              onChange={value => updateFilter('userId', value)}
            />
            <FilterSelect
              id="usage-agent"
              label="Agent"
              value={filters.agentId}
              options={agentOptions}
              allLabel="All agents"
              onChange={value => updateFilter('agentId', value)}
            />
            <FilterSelect
              id="usage-repository"
              label="Repository"
              value={filters.repository}
              options={report?.facets.repositories || []}
              allLabel="All repositories"
              onChange={value => updateFilter('repository', value)}
            />
            <FilterSelect
              id="usage-category"
              label="Category"
              value={filters.category}
              options={(report?.facets.categories || []).map(option => ({
                ...option,
                label: categoryLabels[option.value] || option.label || option.value,
              }))}
              allLabel="All categories"
              onChange={value => updateFilter('category', value)}
            />
            <FilterSelect
              id="usage-skill"
              label="Skill"
              value={filters.skill}
              options={report?.facets.skills || []}
              allLabel="All skills"
              onChange={value => updateFilter('skill', value)}
            />
            <FilterSelect
              id="usage-model"
              label="Model"
              value={filters.model}
              options={report?.facets.models || []}
              allLabel="All models"
              onChange={value => updateFilter('model', value)}
            />
          </div>
        ) : null}
      </form>

      {error ? <div className="app-result app-usage-error" role="alert">{error}</div> : null}
      {isLoading && !report ? <div className="app-loading" role="status">Loading usage analytics...</div> : null}
      {report ? (
        <Tabs className="app-usage-tabs" defaultSelected="overview">
          <Tabs.Header>
            <Tabs.HeaderItem tabId="overview">Overview</Tabs.HeaderItem>
            <Tabs.HeaderItem tabId="models-agents">Models &amp; agents</Tabs.HeaderItem>
            <Tabs.HeaderItem tabId="teams-users">Teams &amp; users</Tabs.HeaderItem>
            <Tabs.HeaderItem tabId="sources">Sources</Tabs.HeaderItem>
            <Tabs.HeaderItem tabId="skills">Skills</Tabs.HeaderItem>
          </Tabs.Header>
          <Tabs.Content>
            <Tabs.ContentItem tabId="overview">
              <div className="app-usage-tab-content">
                <div className="app-usage-cards">
                  <SummaryCard label="Estimated cost" value={costFormatter.format(report.summary.costUsd)} hint="USD pricing snapshot" />
                  <SummaryCard label="Total tokens" value={numberFormatter.format(report.summary.totalTokens)} />
                  <SummaryCard label="Input tokens" value={numberFormatter.format(report.summary.inputTokens)} hint={`${numberFormatter.format(report.summary.cachedInputTokens)} cached`} />
                  <SummaryCard label="Output tokens" value={numberFormatter.format(report.summary.outputTokens)} hint={`${numberFormatter.format(report.summary.reasoningOutputTokens)} reasoning`} />
                  <SummaryCard label="Jobs" value={numberFormatter.format(report.summary.jobs)} hint={`${numberFormatter.format(report.summary.pricedJobs)} priced`} />
                </div>
                {report.summary.unpricedJobs > 0 ? (
                  <div className="app-usage-warning" role="status">
                    {numberFormatter.format(report.summary.unpricedJobs)} {report.summary.unpricedJobs === 1 ? 'job uses' : 'jobs use'} a model without configured pricing and {report.summary.unpricedJobs === 1 ? 'is' : 'are'} excluded from cost totals.
                  </div>
                ) : null}
                <section className="app-usage-panel">
                  <h2>Token usage trend</h2>
                  <DailyUsageChart rows={report.byDay} />
                </section>
              </div>
            </Tabs.ContentItem>
            <Tabs.ContentItem tabId="models-agents">
              <div className="app-usage-breakdowns">
                <BreakdownTable title="Models" columnTitle="Model" rows={report.byModel} labelFor={row => row.model || 'unavailable'} />
                <BreakdownTable title="Agents" columnTitle="Agent" rows={report.byAgent} labelFor={row => row.agentName || agentNames.get(row.agentId || '') || row.agentId || 'unavailable'} />
              </div>
            </Tabs.ContentItem>
            <Tabs.ContentItem tabId="teams-users">
              <div className="app-usage-breakdowns">
                <BreakdownTable title="Teams" columnTitle="Team" rows={report.byTeam} labelFor={row => teamNames.get(row.teamId || '') || (row.teamId === 'personal' ? 'Personal' : row.teamId || 'Personal')} />
                <BreakdownTable title="Users" columnTitle="Cognito user ID" rows={report.byUser} labelFor={row => row.userId || 'unavailable'} />
              </div>
            </Tabs.ContentItem>
            <Tabs.ContentItem tabId="sources">
              <div className="app-usage-breakdowns">
                <BreakdownTable title="Repositories" columnTitle="Primary repository" rows={report.byRepository} labelFor={row => row.repository || 'unavailable'} note="Totals are attributed to the primary repository. Filtering matches any repository included in a job." />
                <BreakdownTable title="Categories" columnTitle="Origin channel" rows={report.byCategory} labelFor={row => categoryLabels[row.category || ''] || row.category || 'unavailable'} />
              </div>
            </Tabs.ContentItem>
            <Tabs.ContentItem tabId="skills">
              <SkillAttributionTable rows={report.bySkill} totalJobs={report.summary.jobs} />
            </Tabs.ContentItem>
          </Tabs.Content>
        </Tabs>
      ) : null}
    </div>
  );
}
